import React, { useEffect, useState } from 'react';
import PropTypes, { any } from 'prop-types';
import { Form } from 'react-form';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';

import Popup from '../../Popup';
import { CheckBox, DateTimePicker, Input, Select, ToggleSwitch } from '../../Form';
import { required } from '../../../utils/validation';
import { getAllowedFormatTypes, updateEventDrawScoringTypeOverrides, isPickleBallSportTypeEvent } from '../../../utils/events';
import {
    divisionFormatTypeIds,
    drawAutofillTypeIds,
    drawEliminationTypeIds,
    eventGenderTypeIds,
    playOffDrawDetailsForAutomatedDraws,
    scoringTypeOverridesForAutomatedDraws,
    MATCHES_GUARANTEED_FLEX_LEAGUE,
    ballTypeIds,
    ballTypes,
} from '../../../utils/eventConstants';
import { allowedMatchTimes } from '../../../utils/allottedTimeConstants';
import ScoringTypeAllottedTimeField from '../EventDrawCreateEditPopup/ScoringTypeAllottedTimeField';
import GroupDrawFormatFields from '../EventDrawCreateEditPopup/GroupDrawFormatFields';

import useDrawEdit from '../EventDrawCreateEditPopup/useDrawEdit';
import { sortObjectsByNameAsc } from '../../../utils/sorting';
import { useStatsig } from '../../hooks/useStatsig';

import styles from '../EventDrawCreateEditPopup/eventDrawCreateEditPopup.scss';
import links from '../../../../config/links';

function AutomatedDrawCreatePopup(props) {
    const {
        submitting,
        onCloseClick,
        onCreateManualDraw,
        sportTypeId,
        isVerifiedEvent,
        eventSchedule,
        eventAllottedMatchTimes,
        selectedCount = 0,
        handleSubmit,
        forceDoubles,
        divisions,
        associatedDivisionIds,
        playersDictByMemberId,
        teamsDict,
        divisionDrawAssociation,
        timeZone,
        eventId,
        eventTypeId,
        awardDrawMedal,
        isFlexLeagueClub,
        allowMomentTimeZoneOverride,
        isHomeCourtQuestionRequired,
    } = props;

    const coreUI = useSelector(({ coreUI }) => coreUI);
    const drawEdit = useSelector(({ drawEdit }) => drawEdit);
    const [showDivisionError, setShowDivisionError] = useState(false);
    const { checkGate, featureGates } = useStatsig();
    const {
        isAllottedTimeFlagEnabled,
        dateToDisplay,
        onChangeScoringType,
        onFormatTypeIdChange,
        onGenderIdChange,
        handleDateChange,
        getScoringFormats,
        onChangeAutoFillType,
        onGroupsCountChange,
        onGroupPlayersAdvancingChangeForGroupDraws,
    } = useDrawEdit({
        sportTypeId,
        eventAllottedMatchTimes,
        isInEditMode: false,
        eventId,
        draw: {},
        timeZone,
        isVerifiedEvent,
        eventTypeId,
        forceDoubles,
        allowMomentTimeZoneOverride,
    });
    const [playOffDrawOptions, setPlayOffDrawOptions] = useState([]);
    const [scoringFormatOverrideOptions, setScoringFormatOverrideOptions] = useState([]);
    const [showIsLocationBased, setShowIsLocationBased] = useState(false);

    const { genderTypes } = coreUI.types.tms;

    const isTimeBasedFormatEnabled = checkGate(featureGates.WEB_TIME_BASED_SETTINGS) && !isPickleBallSportTypeEvent(sportTypeId);
    const isDrawDivisionAssociationEnabled = checkGate(featureGates.DRAW_DIVISION_ASSOCIATION) && divisionDrawAssociation;

    const isAwardingMedalEnabled = awardDrawMedal;

    const displayableDrawEliminationTypes = [
        {
            id: drawEliminationTypeIds.ROUND_ROBIN,
            description: '',
            value: 'round_robin',
            label: 'Round Robin',
            order: drawEliminationTypeIds.ROUND_ROBIN,
        },
        ...(isFlexLeagueClub ? [{
            id: drawEliminationTypeIds.FLEX_LEAGUE,
            description: '',
            value: 'flex_league',
            label: 'Flex League',
            order: drawEliminationTypeIds.FLEX_LEAGUE,
        }] : []),
    ];

    const showBallTypeField = !isPickleBallSportTypeEvent(sportTypeId);

    const { selected, selectedTeams } = drawEdit;
    const selectedPlayersDivisionIds = [];
    const selectedTeamsDivisionIds = [];
    selected.forEach(memberId => {
        const playerDivisions = playersDictByMemberId[memberId]?.divisions;
        if (playerDivisions?.length) {
            playerDivisions.forEach(div => {
                if (!selectedPlayersDivisionIds.includes(div.id)) {
                    selectedPlayersDivisionIds.push(div.id);
                }
            });
        }
    });
    selectedTeams.forEach(teamId => {
        const teamDivisions = teamsDict[teamId]?.divisions;
        if (teamDivisions?.length) {
            teamDivisions.forEach(div => {
                if (!selectedTeamsDivisionIds.includes(div.id)) {
                    selectedTeamsDivisionIds.push(div.id);
                }
            });
        }
    });

    const eventStartDate = moment.utc(eventSchedule.eventStartUtc).tz(eventSchedule?.timeZone.value);
    const eventEndDate = moment.utc(eventSchedule.eventEndUtc).tz(eventSchedule?.timeZone.value);

    const defaultValues = {
        eliminationTypeId: drawEliminationTypeIds.ROUND_ROBIN,
        autofillTypeId: drawAutofillTypeIds.LEVEL_BASED,
        matchesGuaranteed: 2,
        groupsCount: 2,
        formatTypeId: forceDoubles ? divisionFormatTypeIds.DOUBLES : divisionFormatTypeIds.SINGLES,
        genderId: eventGenderTypeIds.ALL,
        ballTypeId: ballTypeIds.YELLOW,
        awardMedal: true,
    };

    const getFilteredDivisions = (values) => {
        let filteredDivisions = divisions.filter(div => div.divisionFormatTypeId === parseInt(values.formatTypeId) && div.genderId === parseInt(values.genderId)).sort(sortObjectsByNameAsc);
        if (selected.length) {
            filteredDivisions = filteredDivisions.filter(div => selectedPlayersDivisionIds.includes(div.id));
        }
        if (selectedTeams.length) {
            filteredDivisions = filteredDivisions.filter(div => selectedTeamsDivisionIds.includes(div.id));
        }
        return filteredDivisions;
    };

    const shouldDivisionBeDisabled = (divId) => {
        return associatedDivisionIds.includes(divId);
    };

    const onFormChange = (formState) => {
        updateWhichPlayOffDrawsToDisplay(formState);
        updateWhichScoringTypeOverridesToDisplay(formState);
    };

    const updateWhichPlayOffDrawsToDisplay = (formState) => {
        const { values } = formState;
        const playOffDrawsToDisplay = playOffDrawDetailsForAutomatedDraws.filter((playOffDrawDetail) => {
            const isDrawEliminationTypeValid = playOffDrawDetail.drawEliminationTypeId === parseInt(values?.eliminationTypeId);

            if (isDrawEliminationTypeValid && playOffDrawDetail.needsExtraFieldsToCheck) {
                const fieldMatchingCriteria = playOffDrawDetail.extraFields.filter((extraField) => {
                    const valueToCheck = values[extraField.name];

                    return valueToCheck && parseInt(values[extraField.name]) !== extraField.value;
                });

                return fieldMatchingCriteria.length > 0;
            }

            return isDrawEliminationTypeValid;
        });

        setPlayOffDrawOptions(playOffDrawsToDisplay);
    };

    const updateWhichScoringTypeOverridesToDisplay = (formState) => {
        const { values } = formState;

        const scoringFormatOverrides = scoringTypeOverridesForAutomatedDraws.filter((scoringFormatOverride) => {
            const isDrawEliminationTypeValid = scoringFormatOverride.drawEliminationTypeId === parseInt(values?.eliminationTypeId);

            if (isDrawEliminationTypeValid && scoringFormatOverride.isForPlayOffDraw) {
                return values[scoringFormatOverride.playOffDrawFieldName];
            }

            return isDrawEliminationTypeValid;
        });

        setScoringFormatOverrideOptions(scoringFormatOverrides);
    };

    const hasSelectedDivision = (values) => {
        // eslint-disable-next-line no-unused-vars
        return Object.entries(values.divisionIds || {}).length ? !!(Object.entries(values.divisionIds).filter(([key, val]) => val).length) : false;
    };

    const onSubmit = (values) => {
        const updatedValues = updateEventDrawScoringTypeOverrides(values, scoringTypeOverridesForAutomatedDraws);

        if (isDrawDivisionAssociationEnabled && !hasSelectedDivision(values)) {
            setShowDivisionError(true);
            return;
        }

        // override submit values for Flex League
        if (parseInt(values.eliminationTypeId) === drawEliminationTypeIds.FLEX_LEAGUE) {
            updatedValues.matchesGuaranteed = MATCHES_GUARANTEED_FLEX_LEAGUE;
            updatedValues.autofillTypeId = drawAutofillTypeIds.LEVEL_BASED;
            updatedValues.eliminationTypeId = drawEliminationTypeIds.AD_HOC;
        }

        // eslint-disable-next-line no-unused-vars
        updatedValues.divisionIds = Object.entries(values.divisionIds || {}).length ? Object.entries(values.divisionIds).filter(([key, val]) => val).map(([key, val]) => key.slice(1)) : [];

        updatedValues.ballTypeId = parseInt(values.ballTypeId) !== parseInt(ballTypeIds.YELLOW) ? values.ballTypeId : null;

        handleSubmit(updatedValues);
    };

    const onChangeEliminationType = (formApi) => (eliminationTypeId) => {
        // Clear playoff field values if already set
        clearAllPlayOffDrawValues(formApi);
        clearAllScoringFormatOverrideValues(formApi);
        setShowIsLocationBased(false);

        if (parseInt(eliminationTypeId) !== drawEliminationTypeIds.ROUND_ROBIN) {
            formApi.reset('autofillTypeId');
            formApi.reset('matchesGuaranteed');
            formApi.reset('groupsCount');
        }
        if (parseInt(eliminationTypeId) === drawEliminationTypeIds.FLEX_LEAGUE && checkGate(featureGates.FLEX_AUTO_DRAW_LOCATION) && isHomeCourtQuestionRequired) {
            setShowIsLocationBased(true);
        }
    };

    const clearAllPlayOffDrawValues = (formApi) => {
        playOffDrawDetailsForAutomatedDraws.forEach((playOffDrawDetail) => {
            formApi.reset(playOffDrawDetail.fieldName);
        }, []);
        formApi.reset('createPlayoffBronzeMatch');
        formApi.reset('groupPlayersAdvancingToPlayoffCount');
    };

    const clearAllScoringFormatOverrideValues = (formApi) => {
        scoringFormatOverrideOptions.forEach((scoringTypeOverride) => {
            const overrideValue = formApi.getValue(scoringTypeOverride.id);

            if (overrideValue) {
                formApi.reset(scoringTypeOverride.id);
            }
        }, []);
    };

    const onPlayOffDrawCheckChange = (formApi) => (value, playOffDrawOption) => {
        if (playOffDrawOption.fieldName === 'createPlayoffDraw') {
            if (!value) {
                formApi.reset('groupPlayersAdvancingToPlayoffCount');
            } else {
                formApi.setValue('groupPlayersAdvancingToPlayoffCount', 1);
                onGroupPlayersAdvancingChangeForGroupDraws(formApi)(1);
            }
        }
    };

    const isDinglesEnabled = checkGate(featureGates.DINGLES);
    const formatTypes = getAllowedFormatTypes({
        coreUI,
        isDinglesEnabled,
    });

    useEffect(() => {
        const formState = {
            values: {
                eliminationTypeId: drawEliminationTypeIds.ROUND_ROBIN,
            },
        };

        if (playOffDrawOptions.length === 0) {
            updateWhichPlayOffDrawsToDisplay(formState);
        }

        if (scoringFormatOverrideOptions.length === 0) {
            updateWhichScoringTypeOverridesToDisplay(formState);
        }
    }, []);

    useEffect(() => {
        const defaultValue = moment.tz.guess();
        if (eventSchedule?.timeZone) {
            moment.tz.setDefault(eventSchedule.timeZone.value);
        }

        return () => {
            moment.tz.setDefault(defaultValue);
        };
    }, [eventSchedule?.timeZone]);

    const validateDivisionDate = (value) => {
        if (value && moment.utc(value)?.isBefore(eventStartDate)) {
            return {
                error: 'Date can\'t be before the event start date and time.',
            };
        }
        if (value && moment.utc(value)?.isAfter(eventEndDate)) {
            return {
                error: 'Date can\'t be after the event end date and time.',
            };
        }
        return null;
    };

    return (
        <Popup
            className="popup-sm"
            popUpClassName={styles.eventDrawCreateEditPopup}
            onCloseClick={onCloseClick}
            config={{
                fullScreen: false,
                noPadding: true,
                scrollable: false,
                showCloseIcon: true,
                title: 'Create automated draw',
                isSideBar: true,
            }}
        >
            <Form onSubmit={onSubmit} defaultValues={defaultValues} onChange={onFormChange}>
                {(formApi) => {
                    const { submitForm, values } = formApi;
                    const isDrawTypeSupported = parseInt(values.eliminationTypeId) === drawEliminationTypeIds.FLEX_LEAGUE ||
                        parseInt(values.eliminationTypeId) === drawEliminationTypeIds.ROUND_ROBIN;

                    return (
                        <form onSubmit={submitForm} id="drawEditForm">
                            <div className={styles.content}>
                                <div className={styles.section}>
                                    <h6 className={styles.title}>
                                        Draw Info
                                    </h6>
                                    <Input
                                        label="Draw Name"
                                        id="name"
                                        field="name"
                                        placeholder="Ex. Men's Singles"
                                        validate={required}
                                        themeV3
                                    />
                                    <DateTimePicker
                                        id="tourDateStart"
                                        field="startDateTimeUtc"
                                        dateLabel="Start date"
                                        timeLabel="Start time"
                                        selected={dateToDisplay}
                                        minDate={eventStartDate}
                                        maxDate={eventEndDate}
                                        handleDateChange={handleDateChange('startDateTimeUtc', formApi)}
                                        themeV3
                                        validate={value => validateDivisionDate(value)}
                                    />
                                    <Select
                                        id="formatTypeId"
                                        label="Game type"
                                        field="formatTypeId"
                                        options={formatTypes}
                                        validate={required}
                                        disabled={forceDoubles}
                                        onChange={onFormatTypeIdChange(formApi)}
                                        themeV3
                                    />
                                    <Select
                                        id="genderId"
                                        label="Gender"
                                        field="genderId"
                                        options={genderTypes}
                                        validate={required}
                                        onChange={onGenderIdChange(formApi)}
                                        themeV3
                                    />
                                    {showBallTypeField && (
                                        <Select
                                            id="ballTypeId"
                                            label="Ball type"
                                            field="ballTypeId"
                                            options={ballTypes}
                                            validate={required}
                                            themeV3
                                        />
                                    )}
                                    {isAwardingMedalEnabled && (
                                            parseInt(values.eliminationTypeId) === drawEliminationTypeIds.SINGLE_ELIMINATION ||
                                            parseInt(values.eliminationTypeId) === drawEliminationTypeIds.DOUBLE_ELIMINATION ||
                                            parseInt(values.eliminationTypeId) === drawEliminationTypeIds.AD_HOC ||
                                            parseInt(values.eliminationTypeId) === drawEliminationTypeIds.FLEX_LEAGUE ||
                                            parseInt(values.eliminationTypeId) === drawEliminationTypeIds.ROUND_ROBIN
                                    ) && (
                                        <CheckBox
                                            id="awardMedal"
                                            formGroupCssClass={`mb10 ${styles.dummy}`}
                                            customLabel="This draw will award medals"
                                            field="awardMedal"
                                        />
                                    )}
                                </div>
                                {isDrawDivisionAssociationEnabled && (
                                    <div className={styles.section}>
                                        <h6 className={styles.title}>
                                            Associated division(s)
                                        </h6>
                                        {showDivisionError && <div className="text-small text-error">You must select at least one division</div>}
                                        {getFilteredDivisions(values).map(div => (
                                            <CheckBox
                                                key={`division${div.id}`}
                                                id={`division${div.id}`}
                                                customLabel={div.name}
                                                field={`divisionIds[_${div.id}]`}
                                                disabled={shouldDivisionBeDisabled(div.id)}
                                            />
                                        ))}
                                        {!getFilteredDivisions(values).length && (
                                            <div className="text-small">There are no divisions which can be associated with this draw&apos;s game type and gender</div>
                                        )}
                                    </div>
                                )}
                                <div className={styles.section}>
                                    <h6 className={styles.title}>
                                        Draw Format
                                    </h6>
                                    <Select
                                        id="eliminationType"
                                        label="Draw Type"
                                        field="eliminationTypeId"
                                        hiddenDefault
                                        options={displayableDrawEliminationTypes}
                                        validate={required}
                                        themeV3
                                        onChange={onChangeEliminationType(formApi)}
                                    />
                                    {isDrawTypeSupported && (
                                        <GroupDrawFormatFields
                                            values={values}
                                            onChangeAutoFillType={onChangeAutoFillType(formApi)}
                                            selectedCount={selectedCount}
                                            onGroupsCountChange={onGroupsCountChange(formApi)}
                                            playOffDrawOptions={playOffDrawOptions}
                                            onPlayOffDrawCheckChange={onPlayOffDrawCheckChange(formApi)}
                                            onGroupPlayersAdvancingChange={onGroupPlayersAdvancingChangeForGroupDraws(formApi)}
                                        />
                                    )}
                                    {showIsLocationBased && (
                                        <CheckBox
                                            id="IsLocationBased"
                                            customLabel="Apply player/team home court location to group placements"
                                            field="IsLocationBased"
                                        />
                                    )}
                                </div>
                                {isDrawTypeSupported && (
                                    <div className={styles.section}>
                                        <div>
                                            <div className={`d-flex aic justify-content-space-between`}>
                                                <h6 className={`${styles.title} mr-auto`}>
                                                    Scoring Formats
                                                </h6>
                                                {(isTimeBasedFormatEnabled || values.isTimeBased) && (
                                                    <ToggleSwitch
                                                        id="isTimeBased"
                                                        field="isTimeBased"
                                                        label="Time Based"
                                                        formGroupCssClass={`mb0 ${styles.timeBasedSwitch}`}
                                                        reverse={false}
                                                        hoverText={(
                                                            <>
                                                                <div className="fw600 fs16 mb8">
                                                                    Time Based Match Scoring Format
                                                                </div>
                                                                <ul>
                                                                    <li>
                                                                        A timed-based match concludes when the allotted time period is met, regardless of the scoring format or current score.
                                                                    </li>
                                                                    <li>
                                                                        Timed-based matches are required to have a minimum duration of 30 minutes.
                                                                    </li>
                                                                    <li>
                                                                        To count toward a player&apos;s Verified UTR rating, a minimum of 4 games must be won by one player or team.
                                                                    </li>
                                                                </ul>
                                                                <div>
                                                                    For more information on Timed-Based match rules, please  <a href={links.verifiedRules} target="_blank" rel="noopener noreferrer">click here</a>.
                                                                </div>
                                                            </>
                                                        )}
                                                    />
                                                )}
                                            </div>
                                            {values.isTimeBased && (
                                                <div className={`mt8 fs14 fw400`}>
                                                    By enabling Timed-Based Scoring, you confirm that all matches in this division will last at least 30 minutes and follow the <a href={links.verifiedRules} target="_blank" rel="noopener noreferrer">UTR timed-based scoring rules</a>.
                                                </div>
                                            )}
                                        </div>
                                        {!isAllottedTimeFlagEnabled && (
                                            <Select
                                                id="scoringTypeId"
                                                label="Default scoring format"
                                                field="scoringTypeId"
                                                options={getScoringFormats(values)}
                                                themeV3
                                            />
                                        )}
                                        {isAllottedTimeFlagEnabled && (
                                            <div className="d-flex align-items-start">
                                                <div className="flex-1">
                                                    <Select
                                                        id="scoringTypeId"
                                                        label="Default scoring format"
                                                        field="scoringTypeId"
                                                        options={getScoringFormats(values)}
                                                        themeV3
                                                        formGroupCssClass={`mb0`}
                                                        validate={required}
                                                        onChange={onChangeScoringType(formApi)}
                                                    />
                                                </div>
                                                <div className="flex-1 ml16">
                                                    <Select
                                                        id="allottedMatchTime"
                                                        field="allottedMatchTime"
                                                        label="Default allotted time"
                                                        options={allowedMatchTimes}
                                                        themeV3
                                                        formGroupCssClass={`mb0`}
                                                        validate={required}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        {isAllottedTimeFlagEnabled && scoringFormatOverrideOptions.map((scoringFormatOverride) => {
                                            return (
                                                <ScoringTypeAllottedTimeField
                                                    key={`override-${scoringFormatOverride.id}`}
                                                    scoringFormatOverride={scoringFormatOverride}
                                                    scoringTypeOptions={getScoringFormats(values)}
                                                    formApi={formApi}
                                                    values={values}
                                                    eventAllottedMatchTimes={eventAllottedMatchTimes}
                                                    disableAllottedTimesField={false}
                                                />
                                            );
                                        })}
                                    </div>
                                )}
                                {!isDrawTypeSupported && (
                                    <div className={styles.section}>
                                        <div className={styles.description}>
                                            Automatic draw creation currently only works for Round Robin and Flex League draw types. For any other types you can create the draw manually.
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className={styles.footer}>
                                {isDrawTypeSupported && (
                                    <>
                                        <button
                                            onClick={onCloseClick}
                                            type="button"
                                            className="btn btn-default text-uppercase flex-1"
                                            disabled={submitting}
                                        >
                                            Back
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary text-uppercase flex-1"
                                            disabled={submitting || !isDrawTypeSupported}
                                        >
                                            {submitting && (<i className="fas fa-circle-notch fa-spin" />)} Create Draw
                                        </button>
                                    </>
                                )}
                                {!isDrawTypeSupported && (
                                    <button
                                        onClick={onCreateManualDraw}
                                        type="button"
                                        className="btn btn-primary text-uppercase flex-1"
                                        disabled={submitting}
                                    >
                                        Create draw manually
                                    </button>
                                )}
                            </div>
                        </form>
                    );
                }}
            </Form>
        </Popup>
    );
}

AutomatedDrawCreatePopup.propTypes = {
    submitting: PropTypes.bool,
    isVerifiedEvent: PropTypes.bool,
    onCloseClick: PropTypes.func.isRequired,
    onCreateManualDraw: PropTypes.func.isRequired,
    eventSchedule: PropTypes.objectOf(any).isRequired,
    sportTypeId: PropTypes.number.isRequired,
    eventAllottedMatchTimes: PropTypes.arrayOf(any),
    selectedCount: PropTypes.number.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    awardDrawMedal: PropTypes.bool,
    isFlexLeagueClub: PropTypes.bool,
    allowMomentTimeZoneOverride: PropTypes.bool,
    isHomeCourtQuestionRequired: PropTypes.bool,
};

AutomatedDrawCreatePopup.defaultProps = {
    submitting: false,
    isVerifiedEvent: false,
    eventAllottedMatchTimes: [],
    awardDrawMedal: false,
    isFlexLeagueClub: false,
    allowMomentTimeZoneOverride: true,
    isHomeCourtQuestionRequired: false,
};

export default AutomatedDrawCreatePopup;
